import { useState } from 'react'
import clsx from 'clsx'
import { Alert, Tabs } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button, StatusChip, useSnackbar } from '@mysaiva/core'
import { useWorkspaceBreadcrumb } from '../../layout/useWorkspaceBreadcrumb'
import { workspacePath } from '../../navigation'
import {
  useApiKey,
  useDeleteApiKey,
  useRotateApiKey,
  useSetApiKeyEnabled,
  useUpdateApiKey
} from '../../api-keys/useApiKeys'
import { SCOPE_GROUPS, SCOPE_GROUP_TONE, getScope } from '../../api-keys/scopeCatalog'
import { formatDate, formatRelative } from '../../api-keys/format'
import { DeveloperPlatformEvent, trackDeveloperPlatformEvent } from '../../analytics/events'
import type { ApiKey, ApiKeySecret, UpdateApiKeyPayload } from '../../api-keys/apiKeys.types'
import { EditApiKeyPanel } from './EditApiKeyPanel'
import { DeleteKeyModal, DisableKeyModal, RotateKeyModal } from './KeyActionModals'
import { KeyUsageTab } from './KeyUsageTab'
import styles from './ApiKeys.module.scss'

type Dialog = 'edit' | 'rotate' | 'disable' | 'delete' | null

function OverviewTab({ apiKey }: { apiKey: ApiKey }) {
  const { t } = useTranslation()
  return (
    <div className={styles.form}>
      <dl className={styles.properties} data-testid="key-properties">
        <div className={styles.property}>
          <dt>{t('developerPlatform.apiKeys.fields.key')}</dt>
          <dd>
            <code className={styles.mono}>{apiKey.maskedKey}</code>
          </dd>
        </div>
        <div className={styles.property}>
          <dt>{t('developerPlatform.apiKeys.fields.environment')}</dt>
          <dd>{t(`developerPlatform.apiKeys.environment.${apiKey.environment}`)}</dd>
        </div>
        <div className={styles.property}>
          <dt>{t('developerPlatform.apiKeys.fields.created')}</dt>
          <dd>
            {apiKey.createdBy.name
              ? t('developerPlatform.apiKeys.meta.createdBy', {
                  date: formatDate(apiKey.createdAt),
                  name: apiKey.createdBy.name
                })
              : formatDate(apiKey.createdAt)}
          </dd>
        </div>
        <div className={styles.property}>
          <dt>{t('developerPlatform.apiKeys.fields.lastUsed')}</dt>
          <dd>
            {apiKey.lastUsedAt
              ? formatRelative(apiKey.lastUsedAt)
              : t('developerPlatform.apiKeys.meta.neverUsed')}
          </dd>
        </div>
        <div className={styles.property}>
          <dt>{t('developerPlatform.apiKeys.fields.expires')}</dt>
          <dd>
            {apiKey.expiresAt
              ? formatDate(apiKey.expiresAt)
              : t('developerPlatform.apiKeys.expiration.never')}
          </dd>
        </div>
      </dl>
      <section className={styles.scopesSection} aria-labelledby="dp-key-scopes-title">
        <h2 id="dp-key-scopes-title" className={styles.sectionTitle}>
          {t('developerPlatform.apiKeys.fields.scopes')}
        </h2>
        <div className={styles.properties} data-testid="key-scopes">
          {apiKey.scopes.map(id => {
            const scope = getScope(id)
            const group = scope ? SCOPE_GROUPS.find(g => g.id === scope.group) : undefined
            return (
              <div key={id} className={styles.scopeDetailRow} data-testid={`key-scope-${id}`}>
                <span className={styles.scopeDetailId}>{id}</span>
                {scope && (
                  <>
                    <span className={styles.scopeDetailSep} aria-hidden="true" />
                    <span className={styles.scopeDetailDesc}>{scope.description}</span>
                    {group && (
                      <StatusChip tone={SCOPE_GROUP_TONE[scope.group]} variant="filled" size="sm">
                        {t(group.labelKey)}
                      </StatusChip>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}

/**
 * Key detail (SAIV-11350): Overview and Usage tabs plus Edit / Rotate /
 * Disable|Enable / Delete with their confirmation flows.
 */
export function ApiKeyDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const { keyId } = useParams<{ keyId: string }>()
  const { data: apiKey, isPending, isError, refetch } = useApiKey(keyId)

  const updateMutation = useUpdateApiKey()
  const rotateMutation = useRotateApiKey()
  const enabledMutation = useSetApiKeyEnabled()
  const deleteMutation = useDeleteApiKey()

  const [dialog, setDialog] = useState<Dialog>(null)
  const [rotated, setRotated] = useState<ApiKeySecret | null>(null)

  useWorkspaceBreadcrumb(
    apiKey
      ? [
          { label: t('developerPlatform.pages.apiKeys'), href: workspacePath('api-keys') },
          { label: apiKey.name }
        ]
      : null
  )

  const listPath = workspacePath('api-keys')

  if (isPending) {
    return (
      <section className={styles.page} data-testid="dp-page-api-key-detail">
        <div
          className={`${styles.panel} ${styles.skeletonRows}`}
          aria-busy="true"
          data-testid="key-detail-loading"
        >
          <span />
          <span />
          <span />
        </div>
      </section>
    )
  }

  if (isError) {
    return (
      <section className={styles.page} data-testid="dp-page-api-key-detail">
        <Alert
          type="error"
          showIcon
          message={t('developerPlatform.apiKeys.errorTitle')}
          description={t('developerPlatform.apiKeys.errorText')}
          action={
            <Button size="small" onClick={() => refetch()} data-testid="key-detail-retry">
              {t('developerPlatform.apiKeys.actions.retry')}
            </Button>
          }
          data-testid="key-detail-error"
        />
      </section>
    )
  }

  if (!apiKey) {
    return (
      <section className={styles.page} data-testid="dp-page-api-key-detail">
        <div className={`${styles.panel} ${styles.notFound}`} data-testid="key-not-found">
          <p>{t('developerPlatform.apiKeys.notFound')}</p>
          <Link to={listPath}>{t('developerPlatform.apiKeys.backToList')}</Link>
        </div>
      </section>
    )
  }

  const isActive = apiKey.status === 'active'

  const handleEdit = (payload: UpdateApiKeyPayload) => {
    updateMutation.mutate(
      { id: apiKey.id, payload },
      {
        onSuccess: () => {
          setDialog(null)
          snackbar.success(t('developerPlatform.apiKeys.snackbar.updated'))
        }
      }
    )
  }

  const handleRotate = () => {
    rotateMutation.mutate(apiKey.id, {
      onSuccess: result => setRotated(result)
    })
  }

  const finishRotate = () => {
    setRotated(null)
    setDialog(null)
    snackbar.success(t('developerPlatform.apiKeys.snackbar.rotated'))
  }

  const handleToggleEnabled = (enabled: boolean) => {
    enabledMutation.mutate(
      { id: apiKey.id, enabled },
      {
        onSuccess: () => {
          setDialog(null)
          snackbar.success(
            t(
              enabled
                ? 'developerPlatform.apiKeys.snackbar.enabled'
                : 'developerPlatform.apiKeys.snackbar.disabled'
            )
          )
        }
      }
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(apiKey, {
      onSuccess: () => {
        setDialog(null)
        snackbar.success(t('developerPlatform.apiKeys.snackbar.deleted'))
        navigate(listPath, { replace: true })
      }
    })
  }

  return (
    <section
      className={clsx(styles.page, dialog === 'edit' && styles.pageShifted)}
      data-testid="dp-page-api-key-detail"
    >
      <header className={styles.detailHeader}>
        <div className={styles.detailTitle}>
          <h1 data-testid="key-detail-name">{apiKey.name}</h1>
          <StatusChip status={apiKey.status} data-testid="key-detail-status">
            {t(`developerPlatform.apiKeys.status.${apiKey.status}`)}
          </StatusChip>
        </div>
        {/* Core secondary / small buttons (32px) as in the design header. */}
        <div className={styles.detailActions}>
          <Button
            size="small"
            icon="Edit1"
            iconSize={16}
            onClick={() => setDialog('edit')}
            data-testid="key-action-edit"
          >
            {t('developerPlatform.apiKeys.actions.edit')}
          </Button>
          <Button
            size="small"
            icon="Refresh"
            iconSize={16}
            onClick={() => setDialog('rotate')}
            data-testid="key-action-rotate"
          >
            {t('developerPlatform.apiKeys.actions.rotate')}
          </Button>
          {isActive ? (
            <Button
              size="small"
              icon="CrossCircle"
              iconSize={16}
              onClick={() => setDialog('disable')}
              data-testid="key-action-disable"
            >
              {t('developerPlatform.apiKeys.actions.disable')}
            </Button>
          ) : (
            <Button
              size="small"
              icon="CheckCircle"
              iconSize={16}
              onClick={() => handleToggleEnabled(true)}
              loading={enabledMutation.isPending}
              data-testid="key-action-enable"
            >
              {t('developerPlatform.apiKeys.actions.enable')}
            </Button>
          )}
          <Button
            size="small"
            icon="Trash1"
            iconSize={16}
            danger
            onClick={() => setDialog('delete')}
            data-testid="key-action-delete"
          >
            {t('developerPlatform.apiKeys.actions.delete')}
          </Button>
        </div>
      </header>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: 'overview',
            label: t('developerPlatform.apiKeys.tabs.overview'),
            children: <OverviewTab apiKey={apiKey} />
          },
          {
            key: 'usage',
            label: t('developerPlatform.apiKeys.tabs.usage'),
            children: <KeyUsageTab keyId={apiKey.id} />
          }
        ]}
        data-testid="key-detail-tabs"
      />

      <EditApiKeyPanel
        open={dialog === 'edit'}
        apiKey={apiKey}
        onClose={() => setDialog(null)}
        onSubmit={handleEdit}
        submitting={updateMutation.isPending}
        error={updateMutation.isError}
      />
      <RotateKeyModal
        apiKey={apiKey}
        open={dialog === 'rotate'}
        onCancel={() => setDialog(null)}
        onConfirm={handleRotate}
        loading={rotateMutation.isPending}
        result={rotated}
        onSecretCopied={() =>
          trackDeveloperPlatformEvent(DeveloperPlatformEvent.keyCopySecret, {
            key_id: apiKey.id,
            environment: apiKey.environment
          })
        }
        onDone={finishRotate}
      />
      <DisableKeyModal
        apiKey={apiKey}
        open={dialog === 'disable'}
        onCancel={() => setDialog(null)}
        onConfirm={() => handleToggleEnabled(false)}
        loading={enabledMutation.isPending}
      />
      <DeleteKeyModal
        apiKey={apiKey}
        open={dialog === 'delete'}
        onCancel={() => setDialog(null)}
        onConfirm={handleDelete}
        loading={deleteMutation.isPending}
      />
    </section>
  )
}
